//Import components
import updateMarkerPosition from './updateMarkerPosition'
import markerPopup from './markerPopup'
import mapboxgl from 'mapbox-gl'

// Updates layer marker position using smooth animation
export default function setMapMarkers(
  dispatch,
  locationsDataRef,
  locationIndexRef,
  mapRef,
  vehiclesLayerDataRef,
  mapPopupVehiclesMarkersRef,
  closeMarkerPopupCenterTimeoutIdRef
) {
  for (let singleMarkerData of vehiclesLayerDataRef.current.features) {
    const coords = singleMarkerData.geometry.coordinates
    const props = singleMarkerData.properties
    const markerId = props.vehicle_id
    let mapPopupVehicleMarker // invisible, dedicated for popup only

    // NOTE: discount marker variant is wired up in setVehiclesLayer via `isDiscount`
    // on feature properties. The backend doesn't expose a discount field yet, so we
    // leave properties.isDiscount unset (falsy) and the layer falls through to the
    // battery icons. When the API adds the field, map it in vehiclesAction.js.

    // Update popup marker
    if (mapPopupVehiclesMarkersRef.current[markerId]) {
      mapPopupVehicleMarker = mapPopupVehiclesMarkersRef.current[markerId] // Get map marker

      updateMarkerPosition(mapPopupVehicleMarker, singleMarkerData, coords, markerId, mapRef, vehiclesLayerDataRef) // Update popup marker position on the map

      let popup = mapPopupVehicleMarker.getPopup()

      // Update popup content when it is opened
      if (popup.isOpen()) {
        popup.setDOMContent(markerPopup(singleMarkerData))
      }
    } else {
      // Create html marker on the map
      const markerEl = document.createElement('div')
      markerEl.className = 'map-marker'
      markerEl.dataset.type = props.type
      markerEl.style.zIndex = 'auto'

      // Create the popup
      const popup = new mapboxgl.Popup({
        offset: [0, -20],
        anchor: 'bottom',
        closeButton: false,
        closeOnClick: true,
        focusAfterOpen: false,
        keepInView: true,
      }).setMaxWidth('307px')

      mapPopupVehiclesMarkersRef.current[markerId] = new mapboxgl.Marker(markerEl).setLngLat(coords).setPopup(popup).addTo(mapRef.current)
      mapPopupVehicleMarker = mapPopupVehiclesMarkersRef.current[markerId] // Get map marker

      // Center map by marker coordinates when popup is opened
      const onPopupOpen = () => {
        if (!vehiclesLayerDataRef.current?.features?.length) {
          return
        }

        const markerExists = markerId => vehiclesLayerDataRef.current.features.find(feature => feature.properties.vehicle_id === markerId)

        if (!markerExists(markerId)) {
          return
        }

        // Toggle the "selected" icon variant on the Mapbox symbol layer.
        if (mapRef.current.getSource('vehiclesSource')) {
          mapRef.current.setFeatureState({ source: 'vehiclesSource', id: markerId }, { selected: true })
        }

        popup.setDOMContent(markerPopup(singleMarkerData)) // Create popup content
        const popupHeight = popup.getElement().clientHeight // Get the height of the popup
        const markerLngLat = mapPopupVehicleMarker.getLngLat() // Get the center coordinates of the mapMarker
        const markerPixel = mapRef.current.project(markerLngLat) // Convert the mapMarker coordinates to pixel coordinates
        const verticalOffset = popupHeight / 2 // Calculate the vertical offset based on the popup height

        // Calculate the final target pixel coordinates to center the popup
        const targetPixel = {
          x: markerPixel.x,
          y: markerPixel.y - verticalOffset,
        }

        const targetLngLat = mapRef.current.unproject(targetPixel) // Convert the target pixel coordinates back to geographical coordinates
        clearTimeout(closeMarkerPopupCenterTimeoutIdRef.current) // Clear timout for map center after marker popup close

        // Center the map to the target coordinates using `flyTo`
        mapRef.current.flyTo({
          center: targetLngLat,
          zoom: mapRef.current.getZoom(), // Maintain the current zoom level
          bearing: mapRef.current.getBearing(), // Maintain the current map rotation
          duration: 2000,
          essential: true,
        })
      }

      // Center map by default coordinates when popup is closed
      const onPopupClose = () => {
        // Clear the "selected" icon variant.
        if (mapRef.current.getSource('vehiclesSource')) {
          mapRef.current.setFeatureState({ source: 'vehiclesSource', id: markerId }, { selected: false })
        }

        // Exit if no location data
        if (!locationsDataRef?.current) {
          return
        }

        const longitude = locationsDataRef.current[Number(locationIndexRef.current)].location.longitude
        const latitude = locationsDataRef.current[Number(locationIndexRef.current)].location.latitude
        const zoom = locationsDataRef.current[Number(locationIndexRef.current)].zoom

        clearTimeout(closeMarkerPopupCenterTimeoutIdRef.current) // Clear timout for map center after marker popup close

        closeMarkerPopupCenterTimeoutIdRef.current = setTimeout(function () {
          mapRef.current.flyTo({
            center: [longitude, latitude],
            zoom: zoom,
            duration: 2000,
            essential: true,
          })
        }, 5000)
      }

      // Add event listeners to the popup
      popup.on('open', onPopupOpen)
      popup.on('close', onPopupClose)
    }
  }

  // Cleanup phase — remove HTML popup markers whose vehicles disappeared from
  // the latest fetch. Without this the mapboxgl.Marker instances (invisible
  // popup anchors) accumulate forever as the fleet rotates: each ghost holds
  // onto its DOM + popup, leaking memory and leaving dead click-targets on
  // the map.
  const aliveIds = new Set(vehiclesLayerDataRef.current.features.map(f => f.properties.vehicle_id))
  for (const id of Object.keys(mapPopupVehiclesMarkersRef.current)) {
    if (aliveIds.has(id)) continue
    mapPopupVehiclesMarkersRef.current[id].remove() // detaches marker + popup from the map
    delete mapPopupVehiclesMarkersRef.current[id]
  }

  // Push the latest features into the symbol layer's source once per call
  // (the loop above only manages the per-vehicle HTML popup markers).
  if (mapRef.current && vehiclesLayerDataRef.current.features?.length > 0 && mapRef.current.getSource('vehiclesSource')) {
    mapRef.current.getSource('vehiclesSource').setData(vehiclesLayerDataRef.current)
  }
}
